import { useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Row, Space, Typography } from 'antd';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import { ErrorLabel, StyledNumberInput } from 'components/new-ui/composed/formInput/styles';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import Notification from 'components/ui/base/notification/Notification';
import { InvoicesStatuses } from 'store/invoices/types';
import useInvoices from 'hooks/useInvoices';
import { InvoiceUtils } from 'utils/InvoiceUtils';
import { Events } from 'utils/events.enum';

type Props = {
    invoiceId: string;
    open: boolean;
    onClose: () => void;
};

const PartiallyPaidModal = ({ open, onClose, invoiceId }: Props) => {
    const { invoice, updateInvoiceStatus } = useInvoices(invoiceId);
    const [amount, setAmount] = useState<number | null>(null);
    const [touched, setTouched] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasReqiredError = touched && amount === null;
    const remainingPayment =
        invoice?.totalAmount && invoice?.amountPaid
            ? InvoiceUtils.getRemainingPayment(invoice.totalAmount, invoice.amountPaid)
            : invoice?.totalAmount;
    const hasMaxAmountPaidError = touched && amount != null && remainingPayment != null && amount >= remainingPayment;
    const hasError = hasReqiredError || hasMaxAmountPaidError;

    const handleCancel = () => {
        setAmount(null);
        setTouched(false);
        onClose();
    };

    const handleSubmit = async () => {
        setTouched(true);

        if (amount === null) {
            return;
        }

        try {
            setIsSubmitting(true);

            await updateInvoiceStatus(invoiceId, {
                status: InvoicesStatuses.PARTIALLY_PAID,
                amountPaid: amount.toString(),
            });

            handleCancel();

            Notification({
                type: 'success',
                messageTitle: 'Invoice Updated',
                description: 'Invoice successfully marked as partially paid.',
            });

            window.EventBus.publish(Events.INVOICE_HISTORY_UPDATED);
        } catch (error) {
            const errMessage = getErrorMessage(error) || DEFAULT_INTERNAL_ERROR_MESSAGE;
            Notification({
                type: 'error',
                messageTitle: 'Operation Failed',
                description: errMessage,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ConfirmationModal
            open={open}
            onCancel={handleCancel}
            onConfirm={handleSubmit}
            confirmationButtonDisabled={hasError}
            confirmationButtonLoading={isSubmitting}
            icon={<CheckOutlined />}
            title="Mark as Partially Paid"
            content={
                <>
                    <Typography.Text
                        style={{
                            textAlign: 'left',
                            width: '100%',
                        }}
                    >
                        Enter the amount that has been paid by customer.
                    </Typography.Text>

                    <Row justify="space-between" align="top" style={{ width: '100%' }}>
                        <Space size={4}>
                            <LabelRequiredSymbol />
                            Amount Paid :
                        </Space>
                        <div style={{ width: 360 }}>
                            <StyledNumberInput
                                min={0}
                                precision={2}
                                placeholder="Enter amount"
                                status={hasError ? 'error' : undefined}
                                value={amount}
                                onChange={(value) => {
                                    setAmount(Number(value));
                                }}
                                onBlur={() => setTouched(true)}
                            />
                            {hasReqiredError && <ErrorLabel>Field is required</ErrorLabel>}
                            {hasMaxAmountPaidError && (
                                <ErrorLabel>
                                    The amount paid must be less than the remaining amount ({remainingPayment}).
                                </ErrorLabel>
                            )}
                        </div>
                    </Row>
                </>
            }
        />
    );
};

export default PartiallyPaidModal;
